import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "@/components/ConfirmModal";
import { LeadDetail, LEAD_STATUSES } from "@/pages/dashboard/Leads";

const badgeClass = (s) => `vg-badge vg-badge-${s.replace(/\s+/g, "-")}`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filterStato, setFilterStato] = useState("");
  const [filterAgent, setFilterAgent] = useState("");
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    api.get("/admin/users").then((r) => setAgents(r.data.filter((u) => u.ruolo !== "admin"))).catch(() => {});
  }, []);

  const reload = () => {
    const params = {};
    if (filterStato) params.stato = filterStato;
    if (filterAgent) params.agent_id = filterAgent;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  };

  useEffect(() => { reload(); }, [filterStato, filterAgent]);

  const unassigned = useMemo(() => leads.filter((l) => !l.agent_id), [leads]);
  const assigned = useMemo(() => leads.filter((l) => l.agent_id), [leads]);

  const updateStatus = async (lead_id, stato) => {
    try {
      await api.put(`/agent/leads/${lead_id}/status`, { stato_lead: stato });
      setLeads((arr) => arr.map((l) => l.id === lead_id ? { ...l, stato_lead: stato } : l));
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore aggiornamento"); }
  };

  const assignLead = async (lead_id, agent_id) => {
    try {
      await api.put(`/admin/leads/${lead_id}/assign`, { agent_id: agent_id || null });
      setLeads((arr) => arr.map((l) => l.id === lead_id ? { ...l, agent_id: agent_id || null } : l));
      toast.success(agent_id ? "Lead assegnato" : "Lead non assegnato");
    } catch { toast.error("Errore assegnazione"); }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/agent/leads/${id}`);
      setSelected(r.data);
    } catch { toast.error("Errore"); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/leads/${pendingDelete.id}`);
      setLeads((arr) => arr.filter((l) => l.id !== pendingDelete.id));
      if (selected?.id === pendingDelete.id) setSelected(null);
      toast.success("Lead eliminato");
    } catch { toast.error("Errore eliminazione"); }
    setPendingDelete(null);
  };

  return (
    <div className="space-y-6 fade-slide">
      <h1 className="text-3xl font-bold text-[#0A0A0A]">Lead globali</h1>
      <div className="flex flex-wrap gap-3">
        <select className="vg-input max-w-xs" value={filterStato} onChange={(e) => setFilterStato(e.target.value)} data-testid="admin-filter-stato">
          <option value="">Tutti gli stati</option>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="vg-input max-w-xs" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} data-testid="admin-filter-agent">
          <option value="">Tutti gli agenti</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.nome} {a.cognome}</option>)}
        </select>
      </div>

      {/* Lead non assegnati */}
      <section data-testid="admin-leads-unassigned-section">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg font-bold text-[#B40101]" style={{ fontFamily: "Outfit" }}>Lead non assegnati</h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-[#B40101] font-semibold">{unassigned.length}</span>
        </div>
        <LeadTable
          leads={unassigned}
          agents={agents}
          onAssign={assignLead}
          onStatus={updateStatus}
          onView={openDetail}
          onDelete={(l) => setPendingDelete(l)}
          emptyMsg="Nessun lead non assegnato."
        />
      </section>

      {/* Lead assegnati */}
      <section data-testid="admin-leads-assigned-section">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "Outfit" }}>Lead assegnati</h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold">{assigned.length}</span>
        </div>
        <LeadTable
          leads={assigned}
          agents={agents}
          onAssign={assignLead}
          onStatus={updateStatus}
          onView={openDetail}
          onDelete={(l) => setPendingDelete(l)}
          emptyMsg="Nessun lead assegnato."
        />
      </section>

      {selected && (
        <LeadDetail
          lead={selected}
          onClose={() => setSelected(null)}
          onDelete={() => setPendingDelete(selected)}
          onSaved={(updated) => {
            setLeads((arr) => arr.map((l) => l.id === updated.id ? updated : l));
            setSelected(updated);
          }}
        />
      )}

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina lead"
        message="Sei sicuro di voler eliminare questo lead? L'operazione non è reversibile."
        confirmLabel="Conferma elimina"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testid="admin-lead-delete-modal"
      />
    </div>
  );
}

function LeadTable({ leads, agents, onAssign, onStatus, onView, onDelete, emptyMsg }) {
  if (leads.length === 0) return <div className="vg-card p-6 text-center text-sm text-gray-500">{emptyMsg}</div>;
  return (
    <div className="vg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3 text-left">Lead</th>
              <th className="px-4 py-3 text-left">Indirizzo</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3 text-left">Assegnato a</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-lead-${l.id}`}>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#0A0A0A]">{l.nome}</p>
                  <p className="text-xs text-gray-500">{l.email}</p>
                </td>
                <td className="px-4 py-3 text-gray-600">{l.indirizzo}</td>
                <td className="px-4 py-3">
                  <select
                    className={`${badgeClass(l.stato_lead)} border-0 cursor-pointer`}
                    value={l.stato_lead}
                    onChange={(e) => onStatus(l.id, e.target.value)}
                    data-testid={`admin-lead-status-${l.id}`}
                  >
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    className="vg-input text-xs py-2 pr-7"
                    value={l.agent_id || ""}
                    onChange={(e) => onAssign(l.id, e.target.value)}
                    data-testid={`admin-lead-assign-${l.id}`}
                    style={{ minWidth: 160 }}
                  >
                    <option value="">— Non assegnato —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.nome} {a.cognome}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => onView(l.id)} className="vg-btn-secondary inline-flex items-center gap-1 text-xs px-3 py-2" data-testid={`admin-lead-view-${l.id}`}>
                      <Eye size={14} /> Dettaglio
                    </button>
                    <button onClick={() => onDelete(l)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-red-200 text-[#B40101] hover:bg-red-50 transition-colors" data-testid={`admin-lead-delete-${l.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
